import React, { useState } from "react";
import {
  Phone,
  MapPin,
  Mail,
  Clock,
  Instagram,
  Youtube,
  MessageCircle,
  ExternalLink,
} from "lucide-react";
import { StoreInfo, Address } from "../types";
import GoogleMap from "./GoogleMap";

interface ContactSectionProps {
  storeInfo: StoreInfo;
}

/**
 * Formats an address object into JSX, only displaying fields that have values.
 * Handles missing or empty address fields gracefully.
 */
const formatAddress = (address: Address): React.ReactNode => {
  const parts: string[] = [];

  // Street address
  if (address.street && address.street.trim()) {
    parts.push(address.street.trim());
  }

  // City, State, ZipCode line
  const cityStateZip: string[] = [];
  if (address.city && address.city.trim()) {
    cityStateZip.push(address.city.trim());
  }
  if (address.state && address.state.trim()) {
    cityStateZip.push(address.state.trim());
  }
  if (address.zipCode && address.zipCode.trim()) {
    cityStateZip.push(address.zipCode.trim());
  }
  if (cityStateZip.length > 0) {
    parts.push(cityStateZip.join(", "));
  }

  // Country
  if (address.country && address.country.trim()) {
    parts.push(address.country.trim());
  }

  // If no address parts available, return a message
  if (parts.length === 0) {
    return <span className="text-gray-400 italic">Address not available</span>;
  }

  // Return formatted address with line breaks
  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {part}
          {index < parts.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  );
};

export const ContactSection: React.FC<ContactSectionProps> = ({
  storeInfo,
}) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Create email content
      const emailSubject = `Contact Form: ${formData.subject}`;
      const emailBody = `Hello Benipal Toys Team,

I would like to get in touch regarding: ${formData.subject}

My Details:
- Name: ${formData.firstName} ${formData.lastName}

Message:
${formData.message}

Looking forward to hearing from you!

Best regards,
${formData.firstName} ${formData.lastName}`;

      // Create mailto link
      const mailtoLink = `mailto:info@benipaltoys.com?subject=${encodeURIComponent(
        emailSubject
      )}&body=${encodeURIComponent(emailBody)}`;

      // Open email app
      window.location.href = mailtoLink;

      // Show success message
      setIsSubmitting(false);
      setIsSubmitted(true);

      // Reset form after a delay
      setTimeout(() => {
        setFormData({
          firstName: "",
          lastName: "",
          subject: "",
          message: "",
        });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error("Error opening email app:", error);
      setIsSubmitting(false);
    }
  };

  const socialMediaLinks = [
    {
      name: "Instagram",
      icon: Instagram,
      url: storeInfo.socialMedia.instagram,
    },
    { name: "Youtube", icon: Youtube, url: storeInfo.socialMedia.youtube },
    {
      name: "WhatsApp Updates",
      icon: MessageCircle,
      url: storeInfo.socialMedia.whatsappChannel,
    },
  ];

  return (
    <section className="py-16 bg-gradient-to-b from-green-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Phone className="h-8 w-8 text-green-500 mr-3" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Get in Touch
            </h2>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We'd love to hear from you! Visit us or reach out anytime.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Store Information */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Store Information
            </h3>

            <div className="space-y-4">
              <div className="flex items-start">
                <MapPin className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">Address</p>
                  <p className="text-gray-600">
                    {formatAddress(storeInfo.address)}
                  </p>
                  <a
                    href="https://www.google.com/maps/dir//Khamanon,+Punjab+141801,+India/@30.8166352,76.2633782,25048m/data=!3m1!1e3!4m8!4m7!1m0!1m5!1m1!1s0x39100726df24c193:0xa31a5429dc21f167!2m2!1d76.3457795!2d30.8166613?entry=ttu&g_ep=EgoyMDI1MTAwOC4wIKXMDSoASAFQAw%3D%3D"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center mt-2 text-green-600 hover:text-green-700 font-medium text-sm transition-colors duration-200"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Get Directions
                  </a>
                </div>
              </div>

              <div className="flex items-start">
                <Phone className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">Phone</p>
                  <div className="space-y-1">
                    {storeInfo.phone && storeInfo.phone.trim() && (
                      <a
                        href={`tel:${storeInfo.phone}`}
                        className="block text-green-600 hover:text-green-700 transition-colors duration-200"
                      >
                        {storeInfo.phone}
                      </a>
                    )}
                    {storeInfo.phone2 && storeInfo.phone2.trim() && (
                      <a
                        href={`tel:${storeInfo.phone2}`}
                        className="block text-green-600 hover:text-green-700 transition-colors duration-200"
                      >
                        {storeInfo.phone2}
                      </a>
                    )}
                    {(!storeInfo.phone || !storeInfo.phone.trim()) &&
                      (!storeInfo.phone2 || !storeInfo.phone2.trim()) && (
                        <span className="text-gray-400 italic">
                          Phone not available
                        </span>
                      )}
                  </div>
                </div>
              </div>

              <div className="flex items-start">
                <Mail className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">Email</p>
                  {storeInfo.email && storeInfo.email.trim() ? (
                    <a
                      href={`mailto:${storeInfo.email}`}
                      className="text-green-600 hover:text-green-700 transition-colors duration-200"
                    >
                      {storeInfo.email}
                    </a>
                  ) : (
                    <span className="text-gray-400 italic">
                      Email not available
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-start">
                <Clock className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">Store Hours</p>
                  {storeInfo.hours.weekdays &&
                  storeInfo.hours.weekdays.trim() ? (
                    <p className="text-gray-600">
                      <span className="text-xs"> Mon - Fri: </span>
                      {storeInfo.hours.weekdays}
                    </p>
                  ) : (
                    <p className="text-gray-400 italic">Hours not available</p>
                  )}
                  {storeInfo.hours.weekends &&
                    storeInfo.hours.weekends.trim() && (
                      <p className="text-gray-600 mt-1">
                        <span className="text-xs"> Sat - Sun: </span>
                        {storeInfo.hours.weekends}
                      </p>
                    )}
                </div>
              </div>
            </div>
          </div>

          {/* Follow Us */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Follow Us</h3>

            <div className="grid grid-cols-2 gap-4">
              {socialMediaLinks.map((social) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200 group"
                  >
                    <IconComponent className="h-5 w-5 text-gray-600 group-hover:text-green-500 transition-colors duration-200" />
                    <span className="ml-2 text-sm font-medium text-gray-700 group-hover:text-green-600 transition-colors duration-200">
                      {social.name}
                    </span>
                  </a>
                );
              })}
            </div>

            <div className="mt-6 p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600 text-center">
                Stay updated with our latest products, offers, and toy news!
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Send us a Message
            </h3>

            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">✅</div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Message Sent!
                </h4>
                <p className="text-gray-600 mb-4">
                  Your email app should open with a pre-filled message. Please
                  send the email to complete your inquiry!
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="text-green-600 hover:text-green-700 font-medium"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="First Name"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Last Name"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder="Subject"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />

                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Message"
                  required
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 ${
                    isSubmitting
                      ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                      : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105"
                  }`}
                >
                  {isSubmitting ? "Opening Email..." : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Google Map Section */}
        <div className="mt-12">
          <GoogleMap />
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
